import React, { useEffect, useRef, useState } from 'react';

const AnimatedAvatar = ({ isSpeaking = false, isListening = false, isThinking = false }) => {
    const [blink, setBlink] = useState(false);
    const [mouthOpen, setMouthOpen] = useState(false);
    const [headY, setHeadY] = useState(0);
    const blinkRef = useRef(null);
    const mouthRef = useRef(null);
    const headRef = useRef(null);

    // Parpadeo natural aleatorio
    useEffect(() => {
        const next = () => {
            const delay = 2200 + Math.random() * 2800;
            blinkRef.current = setTimeout(() => {
                setBlink(true);
                setTimeout(() => setBlink(false), 150);
                next();
            }, delay);
        };
        next();
        return () => clearTimeout(blinkRef.current);
    }, []);

    // Movimiento de boca al hablar
    useEffect(() => {
        clearInterval(mouthRef.current);
        if (!isSpeaking) { setMouthOpen(false); return; }
        mouthRef.current = setInterval(() => setMouthOpen(v => !v), 160);
        return () => clearInterval(mouthRef.current);
    }, [isSpeaking]);

    // Cabeceo suave continuo
    useEffect(() => {
        let t = 0;
        const tick = () => {
            t += 0.025;
            setHeadY(Math.sin(t) * 3.5);
            headRef.current = requestAnimationFrame(tick);
        };
        headRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(headRef.current);
    }, []);

    const ringColor = isSpeaking ? '#22d3ee' : '#2D7A4F';

    return (
        <div className="relative flex items-center justify-center select-none" style={{ width: 176, height: 176 }}>
            {/* Rings de estado */}
            {(isSpeaking || isListening) && (
                <>
                    <div className="absolute rounded-full animate-ping" style={{
                        inset: -6, background: `${ringColor}1A`, animationDuration: '1.8s'
                    }} />
                    <div className="absolute rounded-full animate-ping" style={{
                        inset: -16, background: `${ringColor}0D`, animationDuration: '2.4s', animationDelay: '0.5s'
                    }} />
                </>
            )}

            {/* Fondo del círculo */}
            <div
                className="absolute inset-0 rounded-full"
                style={{
                    background: 'linear-gradient(150deg, #181830 0%, #0f0f1f 100%)',
                    border: `1.5px solid ${ringColor}80`,
                    boxShadow: `inset 0 0 24px rgba(34, 211, 238, 0.08), 0 0 24px ${ringColor}33`,
                }}
            />

            <svg viewBox="0 0 180 180" width="176" height="176" style={{ position: 'relative', zIndex: 1 }} xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <clipPath id="av-clip"><circle cx="90" cy="90" r="87" /></clipPath>
                    <radialGradient id="skin-grad" cx="45%" cy="35%" r="60%">
                        <stop offset="0%" stopColor="#FACCAA" />
                        <stop offset="100%" stopColor="#ECA97A" />
                    </radialGradient>
                    <radialGradient id="cheek-grad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#E8826055" />
                        <stop offset="100%" stopColor="#E8826000" />
                    </radialGradient>
                </defs>

                <g clipPath="url(#av-clip)">

                    {/* ── Cuerpo / traje ── */}
                    <path d="M 0 180 C 0 145 28 128 90 125 C 152 128 180 145 180 180 Z" fill="#2C4A3E" />
                    {/* Camisa */}
                    <path d="M 90 125 L 73 142 L 90 152 L 107 142 Z" fill="#F5F1E8" />
                    {/* Corbata */}
                    <path d="M 90 142 L 86 153 L 90 156 L 94 153 Z" fill="#22d3ee" />
                    {/* Cuello */}
                    <rect x="78" y="108" width="24" height="22" rx="7" fill="url(#skin-grad)" />

                    {/* ── Cabeza animada ── */}
                    <g style={{ transform: `translateY(${headY}px)`, transformOrigin: '90px 80px' }}>

                        {/* Cabeza */}
                        <ellipse cx="90" cy="76" rx="36" ry="38" fill="url(#skin-grad)" />

                        {/* Cabello */}
                        <path d="M 54 70 C 54 42 64 34 90 34 C 116 34 126 42 126 70 C 122 52 110 44 90 44 C 70 44 58 52 54 70 Z" fill="#231810" />
                        {/* Lateral izq */}
                        <path d="M 54 70 C 54 58 57 50 61 44 L 57 73 Z" fill="#231810" />
                        {/* Lateral der */}
                        <path d="M 126 70 C 126 58 123 50 119 44 L 123 73 Z" fill="#231810" />

                        {/* Orejas */}
                        <ellipse cx="54" cy="78" rx="7" ry="9" fill="#ECA97A" />
                        <ellipse cx="54" cy="78" rx="4" ry="6" fill="#D4875E" />
                        <ellipse cx="126" cy="78" rx="7" ry="9" fill="#ECA97A" />
                        <ellipse cx="126" cy="78" rx="4" ry="6" fill="#D4875E" />

                        {/* Cejas — más expresivas */}
                        <path d="M 63 65 Q 72 60 78 63" stroke="#231810" strokeWidth="2.8" fill="none" strokeLinecap="round" />
                        <path d="M 102 63 Q 108 60 117 65" stroke="#231810" strokeWidth="2.8" fill="none" strokeLinecap="round" />

                        {/* ── Ojo izquierdo ── */}
                        <g style={{
                            transform: `scaleY(${blink ? 0.05 : 1})`,
                            transformOrigin: '71px 78px',
                            transition: 'transform 0.07s ease-in-out'
                        }}>
                            <ellipse cx="71" cy="78" rx="8" ry="8" fill="white" />
                            <ellipse cx="72" cy="79" rx="4.5" ry="4.5" fill="#1C1311" />
                            <ellipse cx="74" cy="77" rx="1.6" ry="1.6" fill="white" />
                            {/* Párpado superior */}
                            <path d="M 63 74 Q 71 70 79 74" stroke="#C8904A" strokeWidth="1.2" fill="none" />
                        </g>

                        {/* ── Ojo derecho ── */}
                        <g style={{
                            transform: `scaleY(${blink ? 0.05 : 1})`,
                            transformOrigin: '109px 78px',
                            transition: 'transform 0.07s ease-in-out'
                        }}>
                            <ellipse cx="109" cy="78" rx="8" ry="8" fill="white" />
                            <ellipse cx="110" cy="79" rx="4.5" ry="4.5" fill="#1C1311" />
                            <ellipse cx="112" cy="77" rx="1.6" ry="1.6" fill="white" />
                            {/* Párpado superior */}
                            <path d="M 101 74 Q 109 70 117 74" stroke="#C8904A" strokeWidth="1.2" fill="none" />
                        </g>

                        {/* Nariz */}
                        <ellipse cx="90" cy="90" rx="3" ry="2.2" fill="#C8824A" />
                        <path d="M 84 92 Q 90 96 96 92" stroke="#C8824A" strokeWidth="1.8" fill="none" strokeLinecap="round" />

                        {/* ── Boca ── */}
                        {isSpeaking ? (
                            mouthOpen
                                ? <>
                                    <ellipse cx="90" cy="104" rx="9" ry="7" fill="#9B3A2A" />
                                    <ellipse cx="90" cy="100" rx="9" ry="4" fill="#ECA97A" />
                                    <rect x="83" y="99" width="14" height="3" rx="1.5" fill="white" />
                                  </>
                                : <path d="M 80 103 Q 90 110 100 103" stroke="#9B3A2A" strokeWidth="3" fill="none" strokeLinecap="round" />
                        ) : (
                            <path d="M 80 103 Q 90 111 100 103" stroke="#B0604A" strokeWidth="3" fill="none" strokeLinecap="round" />
                        )}

                        {/* Mejillas */}
                        <ellipse cx="62" cy="93" rx="10" ry="6" fill="rgba(220,100,80,0.13)" />
                        <ellipse cx="118" cy="93" rx="10" ry="6" fill="rgba(220,100,80,0.13)" />

                    </g>

                    {/* Sombra sutil */}
                    <ellipse cx="90" cy="172" rx="38" ry="7" fill="rgba(0,0,0,0.08)" />
                </g>
            </svg>

            {/* Indicador de estado */}
            <div
                className="absolute border-2 border-white rounded-full transition-all duration-300"
                style={{
                    width: 18, height: 18,
                    bottom: 11, right: 11,
                    zIndex: 2,
                    background: isThinking ? '#F59E0B' : '#10B981',
                    boxShadow: isThinking
                        ? '0 0 0 3px rgba(245,158,11,0.3)'
                        : '0 0 0 3px rgba(16,185,129,0.25)',
                }}
            />
        </div>
    );
};

export default AnimatedAvatar;
